import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { DashboardConfig } from '@/features/dashboard/config';
import { fmtInt } from '../utils';
import { Panel } from './Primitives';
import { getAllocationRows, listAllocationUploads, processAllocations, uploadAllocationFile, type AllocationUpload, type ProcessResult } from '../allocations';

/*
 * Upload the export back with the store filled in, and the log of what happened.
 *
 * The log is the point of this panel as much as the upload is: when a store
 * turns out wrong, the question is "what was actually in the file we received",
 * and every line of every upload is kept so it can be answered.
 *
 * Uploading does NOT send. Sending to the mini-games API is the second button,
 * so a file is never lost because the API was down when it arrived.
 */

const STATUS_LABELS: Record<string, string> = {
  pending: 'À envoyer',
  sent: 'Envoyé',
  invalid: 'Inexploitable',
  duplicate: 'Déjà traité',
};

function fmtWhen(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

export function AllocationsPanel({ config }: { config: DashboardConfig }) {
  const qc = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);
  const [message, setMessage] = useState<{ tone: 'ok' | 'warn' | 'error'; text: string } | null>(null);
  const [openUpload, setOpenUpload] = useState<number | null>(null);

  const uploads = useQuery({
    queryKey: ['monopoly-allocations', config.reportId],
    queryFn: () => listAllocationUploads(config.reportId, config.apiBaseUrl),
    retry: false,
  });

  const upload = useMutation({
    mutationFn: (file: File) => uploadAllocationFile(config.reportId, config.apiBaseUrl, file),
    onSuccess: (res) => {
      const { counts, filename, rowCount } = res.data;
      const readBy = (res.data.columnMap as { by?: string } | null)?.by;
      setMessage({
        tone: counts.invalid > 0 ? 'warn' : 'ok',
        text: res.meta.already_uploaded
          ? `« ${filename} » avait déjà été déposé — rien n’a été ajouté.`
          : `« ${filename} » déposé : ${fmtInt(rowCount)} ligne${rowCount > 1 ? 's' : ''}, ${fmtInt(counts.pending)} à envoyer` +
            (counts.duplicate ? `, ${fmtInt(counts.duplicate)} déjà traitée${counts.duplicate > 1 ? 's' : ''}` : '') +
            (counts.invalid > 0 ? `, ${fmtInt(counts.invalid)} inexploitable${counts.invalid > 1 ? 's' : ''}` : '') +
            (readBy === 'position' ? ' · colonnes lues par position (en-têtes non reconnus)' : ''),
      });
      qc.invalidateQueries({ queryKey: ['monopoly-allocations', config.reportId] });
    },
    onError: (err: unknown) => {
      // The API's 400 carries the reason the file could not be read — show it,
      // because "upload failed" alone leaves nobody anything to fix.
      const detail = (err as { response?: { json?: () => Promise<{ error?: { message?: string } }> } }).response;
      if (detail?.json) {
        detail
          .json()
          .then((b) => setMessage({ tone: 'error', text: b?.error?.message ?? 'Le fichier n’a pas pu être déposé.' }))
          .catch(() => setMessage({ tone: 'error', text: 'Le fichier n’a pas pu être déposé.' }));
      } else {
        setMessage({ tone: 'error', text: 'Le fichier n’a pas pu être déposé.' });
      }
    },
  });

  const process = useMutation({
    mutationFn: (uploadId?: number) => processAllocations(config.reportId, config.apiBaseUrl, uploadId),
    onSuccess: (res: ProcessResult) => {
      const d = res.data;
      if (d.blocked) {
        setMessage({ tone: 'warn', text: res.meta.message ?? 'L’API des collections n’est pas encore configurée.' });
      } else if (d.failed > 0) {
        setMessage({ tone: 'error', text: `${fmtInt(d.sent)} envoyée(s), ${fmtInt(d.failed)} en échec : ${d.errors[0]?.error ?? 'erreur inconnue'}` });
      } else if (d.attempted === 0) {
        setMessage({ tone: 'ok', text: 'Rien à envoyer : toutes les lignes exploitables sont déjà parties.' });
      } else {
        setMessage({ tone: 'ok', text: `${fmtInt(d.sent)} ligne${d.sent > 1 ? 's' : ''} envoyée${d.sent > 1 ? 's' : ''} au service des mini-jeux.` });
      }
      qc.invalidateQueries({ queryKey: ['monopoly-allocations', config.reportId] });
      qc.invalidateQueries({ queryKey: ['monopoly-allocation-rows'] });
    },
    onError: () => setMessage({ tone: 'error', text: 'L’envoi a échoué. Réessayez dans un instant.' }),
  });

  const list = uploads.data?.data ?? [];
  const totals = list.reduce((a, u) => ({ pending: a.pending + u.counts.pending, invalid: a.invalid + u.counts.invalid }), { pending: 0, invalid: 0 });
  const api = uploads.data?.meta.api;

  return (
    <>
      <Panel
        title="Attribution en magasin"
        action={
          <div className="mono-filters">
            <input
              ref={fileInput}
              type="file"
              accept=".csv,text/csv,text/plain"
              hidden
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) upload.mutate(file);
                e.target.value = ''; // so the same file can be re-picked
              }}
            />
            <button type="button" className="mono-button" onClick={() => fileInput.current?.click()} disabled={upload.isPending}>
              {upload.isPending ? 'Dépôt en cours…' : 'Déposer le fichier'}
            </button>
            <button type="button" className="mono-button" onClick={() => process.mutate(undefined)} disabled={process.isPending || totals.pending === 0}>
              {process.isPending ? 'Envoi en cours…' : `Envoyer ${totals.pending > 0 ? `(${fmtInt(totals.pending)})` : ''}`.trim()}
            </button>
          </div>
        }
      >
        {message && (
          <p
            className="mono-note"
            role={message.tone === 'error' ? 'alert' : undefined}
            style={{ color: message.tone === 'error' ? 'var(--mono-red)' : undefined }}
          >
            {message.text}
          </p>
        )}
        {api && !api.ready && (
          <p className="mono-note">
            Les fichiers sont conservés, mais l’envoi au service des mini-jeux est indisponible tant que la clé d’API n’est pas configurée (
            {api.missing.join(', ')}).
          </p>
        )}

        {list.length === 0 ? (
          <p className="mono-note">
            Aucun fichier déposé. Exportez les gagnants ci-dessous, complétez les colonnes « format » et « magasins », puis déposez le fichier ici.
          </p>
        ) : (
          <div className="mono-table-wrap">
            <table className="mono-table">
              <thead>
                <tr>
                  <th>Fichier</th>
                  <th>Déposé le</th>
                  <th>Lignes</th>
                  <th>À envoyer</th>
                  <th>Envoyées</th>
                  <th>Déjà traitées</th>
                  <th>Inexploitables</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {list.map((u) => (
                  <tr key={u.id}>
                    <td>{u.filename}</td>
                    <td>{fmtWhen(u.uploadedAt)}</td>
                    <td>{fmtInt(u.rowCount)}</td>
                    <td>{u.counts.pending > 0 ? <span className="mono-pill amber">{fmtInt(u.counts.pending)}</span> : '—'}</td>
                    <td>{u.counts.sent > 0 ? <span className="mono-pill green">{fmtInt(u.counts.sent)}</span> : '—'}</td>
                    <td>{u.counts.duplicate ? <span className="mono-pill grey">{fmtInt(u.counts.duplicate)}</span> : '—'}</td>
                    <td>{u.counts.invalid > 0 ? <span className="mono-pill grey">{fmtInt(u.counts.invalid)}</span> : '—'}</td>
                    <td>
                      <button type="button" className="mono-button" onClick={() => setOpenUpload(openUpload === u.id ? null : u.id)}>
                        {openUpload === u.id ? 'Masquer' : 'Voir les lignes'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="mono-foot">
          Le dépôt enregistre le fichier ligne par ligne ; l’envoi au service des mini-jeux est une étape séparée, relançable tant qu’il reste des lignes à
          envoyer.
        </p>
      </Panel>

      {openUpload !== null && <AllocationRows config={config} uploadId={openUpload} upload={list.find((u) => u.id === openUpload)} />}
    </>
  );
}

/** The lines of one file — the log, for when a store looks wrong. */
function AllocationRows({ config, uploadId, upload }: { config: DashboardConfig; uploadId: number; upload?: AllocationUpload }) {
  const [status, setStatus] = useState('all');
  const rows = useQuery({
    queryKey: ['monopoly-allocation-rows', config.reportId, uploadId, status],
    queryFn: () => getAllocationRows(config.reportId, config.apiBaseUrl, uploadId, status),
    retry: false,
  });

  return (
    <Panel
      title={`Lignes de « ${upload?.filename ?? uploadId} »`}
      action={
        <select className="mono-select" aria-label="Filtrer les lignes par statut" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="all">Toutes les lignes</option>
          <option value="pending">À envoyer</option>
          <option value="sent">Envoyées</option>
          <option value="duplicate">Déjà traitées</option>
          <option value="invalid">Inexploitables</option>
        </select>
      }
    >
      {rows.isPending && <p className="mono-note">Chargement…</p>}
      {rows.data && rows.data.data.length === 0 && <p className="mono-note">Aucune ligne pour ce filtre.</p>}
      {rows.data && rows.data.data.length > 0 && (
        <div className="mono-table-wrap">
          <table className="mono-table">
            <thead>
              <tr>
                <th>Ligne</th>
                <th>Lot</th>
                <th>Format</th>
                <th>Magasin</th>
                <th>Rédemption</th>
                <th>Statut</th>
              </tr>
            </thead>
            <tbody>
              {rows.data.data.map((r) => (
                <tr key={r.id}>
                  <td>{r.line_no}</td>
                  <td>{r.lot ?? '—'}</td>
                  <td>{r.format ?? '—'}</td>
                  <td>{r.magasins ?? '—'}</td>
                  <td className="mono-mono">{r.redeemed_at ?? r.medium_code ?? '—'}</td>
                  <td>
                    <span className={`mono-pill ${r.status === 'sent' ? 'green' : r.status === 'pending' ? 'amber' : 'grey'}`}>{STATUS_LABELS[r.status]}</span>
                    {r.error && <span className="mono-note"> {r.error}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="mono-foot">
        Le contenu d’origine de chaque ligne est conservé tel quel, même lorsqu’elle ne peut pas être envoyée. Une rédemption déjà envoyée n’est jamais
        renvoyée, même dans un autre fichier.
      </p>
    </Panel>
  );
}
